import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

// Мои финансы (22.10.2026): доход из CRM считается сам, расходы семьи и бизнеса, регулярные платежи, долги и план.
const rub = n => Math.round(Number(n || 0)).toLocaleString('ru-RU') + ' ₽';
const today = () => new Date().toLocaleDateString('sv-SE');
const MONTHS = ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'];
const monthName = m => { const [y, mm] = m.split('-').map(Number); return `${MONTHS[mm - 1]} ${y}`; };
const shiftMonth = (m, d) => { const [y, mm] = m.split('-').map(Number); const x = new Date(Date.UTC(y, mm - 1 + d, 1)); return x.toISOString().slice(0, 7); };
const closeDate = n => { if (!n) return '—'; const d = new Date(); d.setMonth(d.getMonth() + n); return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`; };
const DEBT_KINDS = { credit: 'Кредит', card: 'Кредитная карта', mfo: 'МФО', person: 'Долг человеку', tax: 'Налог', fine: 'Штраф', fssp: 'У пристава', other: 'Другое' };
const muted = { fontSize: 12, color: 'var(--text-muted)' };
const Card = ({ title, value, sub, color }) => (
  <div className="card" style={{ flex: '1 1 170px' }}>
    <div style={muted}>{title}</div>
    <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
    {sub && <div style={{ fontSize: 12 }}>{sub}</div>}
  </div>
);

function OpForm({ meta, debts, onSaved }) {
  const [f, setF] = useState({ op_date: today(), kind: 'expense', category: 'Продукты', amount: '', note: '', debt_id: '' });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const cats = f.kind === 'income' ? meta.incomeCats : Object.values(meta.groups).flatMap(g => g.cats);
  const save = async () => {
    if (!(Number(f.amount) > 0)) return;
    await api.post('/finance/ops', { ...f, debt_id: f.category === 'Платёж по долгу' ? f.debt_id || null : null });
    setF(p => ({ ...p, amount: '', note: '' }));
    onSaved();
  };
  return (
    <div className="card" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
      <select value={f.kind} onChange={e => setF(p => ({ ...p, kind: e.target.value, category: e.target.value === 'income' ? meta.incomeCats[0] : 'Продукты' }))}>
        <option value="expense">Расход</option><option value="income">Доход (не из CRM)</option>
      </select>
      <input type="date" value={f.op_date} onChange={e => set('op_date', e.target.value)} />
      <select value={f.category} onChange={e => set('category', e.target.value)}>
        {f.kind === 'income' ? cats.map(c => <option key={c}>{c}</option>)
          : Object.entries(meta.groups).map(([k, g]) => <optgroup key={k} label={g.label}>{g.cats.map(c => <option key={c}>{c}</option>)}</optgroup>)}
      </select>
      {f.category === 'Платёж по долгу' && (
        <select value={f.debt_id} onChange={e => set('debt_id', e.target.value)}>
          <option value="">— какой долг —</option>
          {debts.filter(d => !d.closed).map(d => <option key={d.id} value={d.id}>{d.creditor}</option>)}
        </select>
      )}
      <input type="number" placeholder="Сумма" value={f.amount} onChange={e => set('amount', e.target.value)} style={{ width: 110 }}
             onKeyDown={e => e.key === 'Enter' && save()} />
      <input placeholder="Комментарий" value={f.note} onChange={e => set('note', e.target.value)} style={{ flex: '1 1 160px' }}
             onKeyDown={e => e.key === 'Enter' && save()} />
      <button className="btn btn-primary btn-sm" onClick={save}>Добавить</button>
    </div>
  );
}

function Overview({ s, meta, month, reload }) {
  const pay = async r => {
    await api.post('/finance/ops', { op_date: today().slice(0, 7) === month ? today() : `${month}-${String(r.pay_day || 1).padStart(2, '0')}`,
      kind: r.kind, category: r.category, amount: r.amount - r.paid, note: r.title, recurring_id: r.id });
    reload();
  };
  const payDebt = async d => {
    await api.post('/finance/ops', { op_date: today().slice(0, 7) === month ? today() : `${month}-${String(d.pay_day || 1).padStart(2, '0')}`,
      kind: 'expense', category: 'Платёж по долгу', amount: d.amount - d.paid, note: d.creditor, debt_id: d.id });
    reload();
  };
  const p = s.plan;
  const deficit = p.budget < p.minSum;
  return (
    <>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <Card title="Доход" value={rub(s.income)} sub={`из CRM ${rub(s.crmTotal)}${s.manualIncome ? ` + прочее ${rub(s.manualIncome)}` : ''}`} />
        <Card title="Расходы" value={rub(s.expense)} sub={`семья ${rub(s.byGroup.family)} · бизнес ${rub(s.byGroup.business)} · долги ${rub(s.byGroup.debt)}${s.cardRepay ? ` · погашено кредитки ${rub(s.cardRepay)}` : ''}`} />
        <Card title="Остаток" value={rub(s.rest)} color={s.rest < 0 ? 'var(--red)' : 'var(--green, #16a34a)'} />
        <Card title="Налог НПД 4%" value={rub(s.npd.sum)} sub={`оплатить до ${s.npd.due.split('-').reverse().join('.')}`} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, marginBottom: 16 }}>
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Доход из CRM</h3>
          <div>Клиенты по банкротству: <b>{rub(s.crm.clients)}</b></div>
          <div>Гражданские дела: <b>{rub(s.crm.cases)}</b></div>
          <div>Продажи с сайта: <b>{rub(s.crm.shop)}</b></div>
          <div style={{ ...muted, marginTop: 6 }}>Считается сам по отметкам оплат в CRM</div>
        </div>
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Расходы по статьям</h3>
          {Object.entries(meta.groups).map(([k, g]) => {
            const rows = g.cats.filter(c => s.byCat[c]);
            return rows.length ? <div key={k} style={{ marginBottom: 6 }}><div style={{ fontWeight: 600 }}>{g.label} — {rub(s.byGroup[k])}</div>
              {rows.map(c => <div key={c} style={{ fontSize: 13, display: 'flex', justifyContent: 'space-between' }}><span>{c}</span><span>{rub(s.byCat[c])}</span></div>)}</div> : null;
          })}
          {!s.expense && <div style={muted}>Расходов за месяц пока нет</div>}
        </div>
      </div>

      {month === today().slice(0, 7) && <Envelopes key={s.income + s.expense} />}
      <Split s={s} />

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Регулярные платежи этого месяца</h3>
        {(s.debtsDue || []).map(d => (
          <div key={'d' + d.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '5px 0', borderBottom: '1px solid var(--border, #eee)' }}>
            <span style={{ width: 46, ...muted }}>{d.pay_day ? `${d.pay_day} чис.` : ''}</span>
            <span style={{ flex: 1 }}>{d.creditor} <span style={muted}>· платёж по долгу</span></span>
            <span style={{ whiteSpace: 'nowrap' }}>{rub(d.amount)}</span>
            {d.paid >= d.amount ? <span style={{ color: 'var(--green, #16a34a)', width: 90 }}>✓ оплачено</span>
              : <button className="btn btn-secondary btn-sm" style={{ width: 90 }} onClick={() => payDebt(d)}>Оплачено</button>}
          </div>
        ))}
        <div style={{ ...muted, marginTop: 6 }}>Где указано число — накануне платежа придёт задача; закрыли задачу — платёж записан сам.</div>
        {!s.recurring.length && <div style={muted}>Добавьте во вкладке «Регулярные»: аренда, коммуналка, садик, связь, сервер, реклама…</div>}
        {s.recurring.map(r => (
          <div key={r.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '5px 0', borderBottom: '1px solid var(--border, #eee)' }}>
            <span style={{ width: 46, ...muted }}>{r.pay_day ? `${r.pay_day} чис.` : ''}</span>
            <span style={{ flex: 1 }}>{r.title} <span style={muted}>· {r.category}</span></span>
            <span style={{ whiteSpace: 'nowrap' }}>{rub(r.amount)}</span>
            {r.paid >= r.amount ? <span style={{ color: 'var(--green, #16a34a)', width: 90 }}>✓ оплачено</span>
              : <button className="btn btn-secondary btn-sm" style={{ width: 90 }} onClick={() => pay(r)}>Оплачено</button>}
          </div>
        ))}
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Долги и план погашения</h3>
        {!s.debts.count ? <div style={muted}>Долгов не внесено — вкладка «Долги»</div> : <>
          <div style={{ marginBottom: 8 }}>Всего долгов: <b>{rub(s.debts.balance)}</b> ({s.debts.count}){s.debts.overdue ? <span style={{ color: 'var(--red)' }}> · с просрочкой: {s.debts.overdue}</span> : null}</div>
          <div style={{ fontSize: 13, marginBottom: 8 }}>
            Средний доход за 3 месяца {rub(p.avgIncome)} − регулярные расходы {rub(p.mandatory)} − налог {rub(p.npd)} = <b>на долги {rub(p.budget)}</b> в месяц
            {' '}(минимальные платежи {rub(p.minSum)})
          </div>
          {deficit && <div style={{ color: 'var(--red)', marginBottom: 8 }}>
            Не хватает {rub(p.minSum - p.budget)} в месяц даже на минимальные платежи. Нужно сократить расходы, поднять доход или договариваться с кредиторами (реструктуризация, отсрочка).</div>}
          <table><thead><tr><th>Очередь</th><th>Кредитор</th><th>Ставка</th><th>Закроется</th></tr></thead>
            <tbody>{p.order.map((d, i) => <tr key={d.id}><td>{i + 1}</td><td>{d.creditor}{d.overdue && <span style={{ color: 'var(--red)' }}> · просрочка</span>}</td>
              <td>{d.rate ? `${d.rate}%` : '—'}</td><td>{deficit ? '—' : closeDate(d.month)}</td></tr>)}</tbody></table>
          {!deficit && p.months && <div style={{ fontSize: 13, marginTop: 8 }}>Все долги закроются через <b>{p.months} мес.</b> ({closeDate(p.months)}), переплата процентов ≈ {rub(p.interest)}.
            Всё, что остаётся сверх минимальных платежей, — в долг №1 в очереди; закрыли его — его платёж идёт в следующий.</div>}
        </>}
      </div>
    </>
  );
}

// «Куда отложить сейчас»: деньги, пришедшие с начала учёта, по конвертам — налог, платежи по датам, жизнь, реклама, долг №1
function Envelopes() {
  const [e, setE] = useState(null);
  useEffect(() => { api.get('/finance/envelopes').then(r => setE(r.data)).catch(() => {}); }, []);
  if (!e) return null;
  const dm = d => d ? d.slice(8) + '.' + d.slice(5, 7) : '';
  return (
    <div className="card" style={{ marginBottom: 16, borderColor: 'var(--primary, #2563eb)' }}>
      <h3 style={{ marginTop: 0 }}>💰 Куда отложить сейчас — на руках {rub(e.available)}</h3>
      <div style={{ ...muted, marginBottom: 6 }}>Пришло с {dm(e.from)}: {rub(e.income)}, записано расходов {rub(e.spent)}.
        Деньги на руках до начала учёта внесите доходом «Остаток на руках» — они тоже разойдутся по конвертам.</div>
      {e.items.map((i, k) => (
        <div key={k} style={{ display: 'flex', gap: 10, alignItems: 'baseline', padding: '5px 0', borderBottom: '1px solid var(--border, #eee)' }}>
          <span style={{ width: 46, ...muted }}>{dm(i.due)}</span>
          <span style={{ flex: 1 }}>{i.title}{i.late && <span style={{ color: 'var(--red)' }}> · срок прошёл</span>}</span>
          <b style={{ whiteSpace: 'nowrap', color: i.got < i.need ? 'var(--red)' : undefined }}>{rub(i.got)}{i.got < i.need ? ` из ${rub(i.need)}` : ''}</b>
        </div>
      ))}
      {e.rest > 0 && <div style={{ marginTop: 8 }}>Остаток досрочно в долг <b>{e.restTo}</b>: <b>{rub(e.rest)}</b></div>}
      <div style={{ ...muted, marginTop: 6 }}>Пришли новые деньги — в задачах на сегодня появится «💰 Пришло … — куда отложить» с этим списком.</div>
    </div>
  );
}

// Распределение дохода месяца по очереди: налог → жизнь → обязательные платежи по долгам → рост (реклама) → досрочно в долг №1
const ADS_MAX = 15000, ADS_SHARE = 0.3;
function Split({ s }) {
  let left = s.income;
  const take = need => { const got = Math.max(Math.min(need, left), 0); left -= got; return got; };
  const fixed = s.recurring.filter(r => r.kind === 'expense').reduce((a, r) => a + r.amount, 0);
  const rows = [
    ['Налог НПД 4%', s.npd.sum, 'отложить до 28 числа следующего месяца'],
    ['Жизнь и обязательные траты', fixed, 'вкладка «Регулярные»'],
    ['Обязательные платежи по долгам', s.plan.minSum, 'ипотека, кредит, графики у пристава'],
  ].map(([t, need, hint]) => ({ t, need, hint, got: take(need) }));
  const free = Math.max(left, 0);
  const ads = Math.min(ADS_MAX, Math.round(free * ADS_SHARE));
  rows.push({ t: 'Рост: реклама', need: ads, got: take(ads), hint: `${ADS_SHARE * 100}% свободного, не больше ${rub(ADS_MAX)}; оставлять, только если окупается договорами` });
  const first = s.plan.order[0];
  rows.push({ t: `Досрочно в долг: ${first ? first.creditor : '—'}`, need: left, got: take(left), hint: 'всё, что осталось' });
  const short = rows.slice(0, 3).reduce((a, r) => a + r.need - r.got, 0);
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h3 style={{ marginTop: 0 }}>Как распределить доход месяца — {rub(s.income)}</h3>
      {rows.map(r => (
        <div key={r.t} style={{ display: 'flex', gap: 10, alignItems: 'baseline', padding: '5px 0', borderBottom: '1px solid var(--border, #eee)' }}>
          <span style={{ flex: 1 }}>{r.t}<div style={muted}>{r.hint}</div></span>
          <b style={{ whiteSpace: 'nowrap', color: r.got < r.need ? 'var(--red)' : undefined }}>{rub(r.got)}{r.got < r.need ? ` из ${rub(r.need)}` : ''}</b>
        </div>
      ))}
      <div style={{ marginTop: 8, fontSize: 13 }}>
        {short > 0 ? <span style={{ color: 'var(--red)' }}>Не хватает {rub(short)} на обязательное. Рекламу в этом месяце не запускать, каждый новый договор закрывает дыру.</span>
          : <>Точка безубыточности — <b>{rub(rows[0].need + rows[1].need + rows[2].need)}</b> в месяц. Всё сверх неё работает на рост и долги.</>}
      </div>
    </div>
  );
}

// Выписка Сбера по кредитной карте: PDF → проверить статьи → записать (повторная загрузка той же выписки дублей не даёт)
function StatementImport({ meta, onDone }) {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [debt, setDebt] = useState('');
  const cats = ['Погашение карты', ...Object.values(meta.groups).flatMap(g => g.cats).filter(c => c !== 'Платёж по долгу'), ...meta.incomeCats];
  const upload = async f => {
    if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append('file', f);
    try {
      const r = await api.post('/finance/statement', fd);
      setRes({ ...r.data, items: r.data.items.map(x => ({ ...x, skip: x.dup })) });
      setDebt(r.data.debt || '');
    } catch (e) { alert(e.response?.data?.error || 'Не удалось прочитать PDF'); }
    setBusy(false);
  };
  const setItem = (i, patch) => setRes(r => ({ ...r, items: r.items.map(x => x.i === i ? { ...x, ...patch } : x) }));
  const save = async () => {
    const r = await api.post(`/finance/statement/${res.id}/import`, { items: res.items, debt: Number(debt) || null });
    alert(`Записано операций: ${r.data.added}`);
    setRes(null); onDone();
  };
  if (!res) return (
    <div className="card" style={{ marginBottom: 16, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
      <b>Выписка Сбера (PDF)</b>
      <input type="file" accept="application/pdf" disabled={busy} onChange={e => upload(e.target.files[0])} />
      <span style={muted}>{busy ? 'Читаю выписку…' : 'раз в месяц: СберБанк Онлайн → кредитная карта → выписка за месяц'}</span>
    </div>
  );
  const take = res.items.filter(x => !x.skip);
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h3 style={{ marginTop: 0 }}>Выписка: найдено {res.items.length} операций</h3>
      {!res.items.length && <div style={{ color: 'var(--red)' }}>Операции не распознаны — формат выписки пока незнаком, напишите Claude, он подстроит разбор.</div>}
      <div className="table-wrap" style={{ maxHeight: 420, overflowY: 'auto' }}><table>
        <thead><tr><th></th><th>Дата</th><th>Сбер</th><th>Описание</th><th>Сумма</th><th>Статья</th></tr></thead>
        <tbody>{res.items.map(x => (
          <tr key={x.i} style={{ opacity: x.skip ? 0.45 : 1 }}>
            <td><input type="checkbox" checked={!x.skip} disabled={x.dup} onChange={e => setItem(x.i, { skip: !e.target.checked })} title={x.dup ? 'уже загружено' : ''} /></td>
            <td style={{ whiteSpace: 'nowrap' }}>{x.date.split('-').reverse().join('.')}</td>
            <td style={{ fontSize: 12 }}>{x.sber_cat}</td>
            <td style={{ fontSize: 12 }}>{x.desc}{x.dup && <span style={muted}> · уже загружено</span>}</td>
            <td style={{ whiteSpace: 'nowrap', color: x.kind === 'income' ? 'var(--green, #16a34a)' : undefined }}>{x.kind === 'income' ? '+' : '−'}{rub(x.amount)}</td>
            <td><select value={x.category || ''} onChange={e => setItem(x.i, { category: e.target.value })}>{cats.map(c => <option key={c}>{c}</option>)}</select></td>
          </tr>))}
        </tbody></table></div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
        <label>Задолженность по карте на конец выписки: <input type="number" value={debt} onChange={e => setDebt(e.target.value)} style={{ width: 120 }} /> ₽</label>
        <button className="btn btn-primary btn-sm" disabled={!take.length} onClick={save}>Записать {take.length}</button>
        <button className="btn btn-secondary btn-sm" onClick={() => setRes(null)}>Отмена</button>
      </div>
      <div style={{ ...muted, marginTop: 6 }}>Покупки по карте идут в расходы по статьям (для анализа), «+» — погашение карты. Задолженность обновит остаток долга по карте.</div>
    </div>
  );
}

function Ops({ month, reload }) {
  const [list, setList] = useState([]);
  const load = useCallback(() => api.get('/finance/ops', { params: { month } }).then(r => setList(r.data)), [month]);
  useEffect(() => { load(); }, [load]);
  const del = async o => { if (window.confirm('Удалить операцию?')) { await api.delete(`/finance/ops/${o.id}`); load(); reload(); } };
  return (
    <div className="card"><div className="table-wrap"><table>
      <thead><tr><th>Дата</th><th>Статья</th><th>Сумма</th><th>Комментарий</th><th></th></tr></thead>
      <tbody>{list.map(o => (
        <tr key={o.id}>
          <td style={{ whiteSpace: 'nowrap' }}>{o.op_date.split('-').reverse().join('.')}</td>
          <td>{o.category}{o.creditor && <span style={muted}> · {o.creditor}</span>}</td>
          <td style={{ whiteSpace: 'nowrap', color: o.kind === 'income' ? 'var(--green, #16a34a)' : undefined }}>{o.kind === 'income' ? '+' : '−'}{rub(o.amount)}</td>
          <td style={{ fontSize: 13 }}>{o.note}</td>
          <td><button className="btn btn-secondary btn-sm" onClick={() => del(o)}>✕</button></td>
        </tr>))}
      </tbody></table></div>
      {!list.length && <div className="empty-state"><p>Операций за месяц нет</p></div>}
    </div>
  );
}

// Простая редактируемая таблица для регулярных платежей и долгов
function EditList({ path, cols, empty, blank, onChange }) {
  const [list, setList] = useState([]);
  const [add, setAdd] = useState(blank);
  const load = useCallback(() => api.get(`/finance/${path}`).then(r => setList(r.data)), [path]);
  useEffect(() => { load(); }, [load]);
  const upd = async (row, k, v) => { await api.put(`/finance/${path}/${row.id}`, { [k]: v }); load(); onChange(); };
  const create = async () => { await api.post(`/finance/${path}`, add); setAdd(blank); load(); onChange(); };
  const del = async row => { if (window.confirm('Удалить?')) { await api.delete(`/finance/${path}/${row.id}`); load(); onChange(); } };
  const input = (c, val, on) => c.options
    ? <select value={val ?? ''} onChange={e => on(e.target.value)}>{c.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
    : c.type === 'bool' ? <input type="checkbox" checked={!!val} onChange={e => on(e.target.checked)} />
    : <input type={c.type || 'text'} defaultValue={val ?? ''} key={String(val)} style={{ width: c.w || 110 }} onBlur={e => e.target.value !== String(val ?? '') && on(e.target.value)} />;
  return (
    <div className="card"><div className="table-wrap"><table>
      <thead><tr>{cols.map(c => <th key={c.k}>{c.label}</th>)}<th></th></tr></thead>
      <tbody>
        {list.map(row => <tr key={row.id}>{cols.map(c => <td key={c.k}>{input(c, row[c.k], v => upd(row, c.k, v))}</td>)}
          <td><button className="btn btn-secondary btn-sm" onClick={() => del(row)}>✕</button></td></tr>)}
        <tr>{cols.map(c => <td key={c.k}>{c.type === 'bool' ? null : c.options
          ? <select value={add[c.k] ?? ''} onChange={e => setAdd(p => ({ ...p, [c.k]: e.target.value }))}>{c.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
          : <input type={c.type || 'text'} value={add[c.k] ?? ''} placeholder={c.label} style={{ width: c.w || 110 }} onChange={e => setAdd(p => ({ ...p, [c.k]: e.target.value }))} />}</td>)}
          <td><button className="btn btn-primary btn-sm" onClick={create}>Добавить</button></td></tr>
      </tbody></table></div>
      {!list.length && <div style={{ ...muted, marginTop: 8 }}>{empty}</div>}
    </div>
  );
}

export default function FinancePage() {
  const [month, setMonth] = useState(today().slice(0, 7));
  const [tab, setTab] = useState('overview');
  const [meta, setMeta] = useState(null);
  const [s, setS] = useState(null);
  const [debts, setDebts] = useState([]);
  const reload = useCallback(() => {
    api.get('/finance/summary', { params: { month } }).then(r => setS(r.data));
    api.get('/finance/debts').then(r => setDebts(r.data));
  }, [month]);
  useEffect(() => { api.get('/finance/meta').then(r => setMeta(r.data)); }, []);
  useEffect(() => { reload(); }, [reload]);
  if (!meta || !s) return <div className="card">Загрузка…</div>;

  const expCats = Object.values(meta.groups).flatMap(g => g.cats).filter(c => c !== 'Платёж по долгу');
  const tabs = [['overview', 'Обзор'], ['ops', 'Операции'], ['recurring', 'Регулярные'], ['debts', 'Долги']];
  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <h1 className="page-title">Мои финансы</h1>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setMonth(shiftMonth(month, -1))}>‹</button>
          <b style={{ minWidth: 120, textAlign: 'center' }}>{monthName(month)}</b>
          <button className="btn btn-secondary btn-sm" onClick={() => setMonth(shiftMonth(month, 1))}>›</button>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {tabs.map(([k, l]) => <button key={k} className={`btn btn-sm ${tab === k ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab(k)}>{l}</button>)}
      </div>
      {(tab === 'overview' || tab === 'ops') && <OpForm meta={meta} debts={debts} onSaved={reload} />}
      {tab === 'overview' && <Overview s={s} meta={meta} month={month} reload={reload} />}
      {tab === 'ops' && <StatementImport meta={meta} onDone={reload} />}
      {tab === 'ops' && <Ops key={s.expense + s.manualIncome} month={month} reload={reload} />}
      {tab === 'recurring' && <EditList path="recurring" onChange={reload}
        empty="Обязательные траты каждый месяц: аренда/ипотека, коммуналка, садик/школа, связь, сервер, подписки, реклама. По ним считается, сколько остаётся на долги."
        blank={{ kind: 'expense', category: 'Жильё и коммуналка', title: '', amount: '', pay_day: '' }}
        cols={[{ k: 'title', label: 'Что', w: 180 }, { k: 'category', label: 'Статья', options: expCats.map(c => [c, c]) },
               { k: 'amount', label: 'Сумма', type: 'number' }, { k: 'pay_day', label: 'Число', type: 'number', w: 60 }, { k: 'active', label: 'Действует', type: 'bool' }]} />}
      {tab === 'debts' && <EditList path="debts" onChange={reload}
        empty="Внесите все долги: кредиты, карты, МФО, долги людям, налоги, штрафы. Ставка — % годовых, платёж — обязательный в месяц."
        blank={{ creditor: '', kind: 'credit', balance: '', rate: '', min_payment: '', pay_day: '', overdue_since: '' }}
        cols={[{ k: 'creditor', label: 'Кому', w: 160 }, { k: 'kind', label: 'Вид', options: Object.entries(DEBT_KINDS) },
               { k: 'balance', label: 'Остаток', type: 'number' }, { k: 'rate', label: 'Ставка %', type: 'number', w: 70 },
               { k: 'min_payment', label: 'Платёж/мес', type: 'number' }, { k: 'pay_day', label: 'Число', type: 'number', w: 60 },
               { k: 'overdue_since', label: 'Просрочка с', type: 'date', w: 140 }, { k: 'note', label: 'Заметка', w: 160 }, { k: 'closed', label: 'Закрыт', type: 'bool' }]} />}
    </div>
  );
}
